import volkswagenLogo from '@/assets/volkswagen-logo.png';
import { mockCountries } from '@/components/CardCountry/mockData';
import { AccidentType } from '@/components/AccidentItemHSP';
import { RaportHSPHistoryData } from './RaportHSPHistory';
import { RaportHSPSummaryData } from './RaportHSPSummary/RaportHSPSummary';
import { RaportHSPServiceData } from './RaportHSPService/RaportHSPService';
import { mockPhotos } from '../PhotoGallery/mockData';

export interface RaportHSPData {
  // Dane podstawowe
  vehicleBrand: string;
  vehicleModel: string;
  vinNumber: string;
  orderNumber: string;
  packageType: string;
  reportDate: string;
  pagesCount: number;
  logoUrl: string;

  // Dane identyfikacyjne pojazdu
  vehicleIdentification: {
    brand: string;
    model: string;
    generation: string;
    version: string;
    productionYear: string;
    modelYear: string;
    vin: string;
    countryOfOrigin: string;
    faceliftYear: string;
    productionStartDate: string;
    productionEndDate: string;
  };

  // Podsumowanie HSP
  summary: RaportHSPSummaryData;

  // Historia pojazdu
  history: RaportHSPHistoryData;

  // Historia serwisowa
  service: RaportHSPServiceData;

  // Historia szkód pojazdu
  accidents: Array<{
    date: string;
    damageValue?: string;
    damageCountry?: string;
    damageType?: AccidentType;
    damageLocation?: string;
    mileage?: string;
    description?: string;
  }>;

  // Status prawny
  legal: {
    polishDatabases: Array<{
      name: string;
      variant: 'success' | 'danger';
      description: string;
      badgeText: string;
    }>;
    verificationDate: string;
    overallStatus: 'success' | 'warning' | 'danger';
    countries: typeof mockCountries;
  };
}

export const mockHSPData: RaportHSPData = {
  // Dane podstawowe
  vehicleBrand: 'Volkswagen',
  vehicleModel: 'Tiguan 2.0 TDI',
  vinNumber: 'WVGZZZ5NZPW123456',
  orderNumber: '50004',
  packageType: 'HSP (History, Service, Police)',
  reportDate: '28.01.2025',
  pagesCount: 45,
  logoUrl: volkswagenLogo,

  // Dane identyfikacyjne pojazdu
  vehicleIdentification: {
    brand: 'Volkswagen',
    model: 'Tiguan',
    generation: 'VI',
    version: '2.0 TDI 115 KM SCR',
    productionYear: '2022',
    modelYear: '2019',
    vin: 'WVGZZZ5NZPW123456',
    countryOfOrigin: 'Niemcy',
    faceliftYear: '2020',
    productionStartDate: '2016',
    productionEndDate: '2023',
  },

  // Podsumowanie HSP
  summary: {
    overallStatus: 'success' as 'success' | 'warning' | 'danger',
    totalChecks: 23,
    foundIssues: 2,
    clearedChecks: 21,
    recommendations: [
      'Pojazd ma czystą historię w większości baz',
      'Znaleziono drobne uwagi w historii serwisowej',
      'Zalecane dokładne sprawdzenie zapisów serwisowych',
    ],
    damageCount: 0,
    lastMileage: '85 000 km',
    lastMileageDate: '13.05.2023',
  },

  // Historia pojazdu
  history: {
    accidents: [
      {
        date: '14.05.2017',
        damageValueMin: 6000,
        damageValueMax: 7000,
        damageCountry: 'Wielka Brytania',
        damageType: AccidentType.MECHANICAL_DAMAGE,
        damageLocation: ['Lewy', 'przedni bok'],
        mileage: '85 200 km',
        description:
          'Do tej sekcji uszkodzeń kwalifikują się zarówno elementy odejmowalne nadwozia jak zderzak przedni czy reflektory ale również elementy układu hamulcowego, kierowniczego, zawieszenia.',
        currency: 'PLN',
        photoGallery: {
          galleryVariant: 'damage-hsp',
          photos: mockPhotos,
        },
      },
      {
        date: '08.11.2019',
        damageValueMin: 3500,
        damageValueMax: 3500,
        damageCountry: 'Polska',
        damageType: AccidentType.BODY_DAMAGE,
        damageLocation: ['Prawy', 'tylny bok'],
        mileage: '156 780 km',
        description:
          'Uszkodzenie elementów nadwozia w wyniku kolizji parkingowej. Wymagana wymiana drzwi oraz naprawa lakieru.',
        currency: 'PLN',
      },
      {
        date: '22.07.2021',
        damageValueMin: 15000,
        damageValueMax: 20000,
        damageCountry: 'Niemcy',
        damageType: AccidentType.ACCIDENT,
        damageLocation: ['Przód pojazdu'],
        mileage: '67 450 km',
        description:
          'Poważny wypadek drogowy z udziałem dwóch pojazdów. Znaczne uszkodzenia przedniej części pojazdu.',
        currency: 'PLN',
      },
    ],
  },

  // Historia serwisowa
  service: {
    totalRecords: 12,
    lastServiceDate: '2024-11-15',
    majorServices: [
      {
        date: '2024-11-15',
        mileage: 85000,
        type: 'Przegląd główny',
        description: 'Wymiana oleju, filtrów, sprawdzenie układu hamulcowego',
        cost: 1200,
        workshop: 'ASO Volkswagen Warszawa',
        status: 'completed',
      },
      {
        date: '2024-05-20',
        mileage: 82000,
        type: 'Naprawa',
        description: 'Wymiana klocków hamulcowych przód',
        cost: 450,
        workshop: 'Warsztat Hamulcowy XYZ',
        status: 'completed',
      },
      {
        date: '2023-10-12',
        mileage: 75000,
        type: 'Przegląd okresowy',
        description: 'Standardowy przegląd 75k km',
        cost: 800,
        workshop: 'ASO Volkswagen Kraków',
        status: 'completed',
      },
    ],
    upcomingServices: [
      {
        type: 'Przegląd główny',
        estimatedDate: '2025-05-15',
        estimatedMileage: 90000,
        description: 'Zalecany przegląd co 5000 km',
      },
    ],
    recalls: [
      {
        date: '2022-03-10',
        description: 'Akcja serwisowa - aktualizacja oprogramowania silnika',
        status: 'completed',
        recallNumber: 'VW-2022-001',
      },
    ],
  },

  // Historia szkód pojazdu (dodatkowe szkody)
  accidents: [
    {
      date: '15.01.2022',
      damageValue: '8500 - 12 000 PLN',
      damageCountry: 'Francja',
      damageType: AccidentType.ENGINE_DAMAGE,
      damageLocation: 'Komora silnika',
      mileage: '203 450 km',
      description:
        'Uszkodzenie silnika w wyniku przegrzania. Wymagana wymiana elementów układu chłodzenia oraz częściowa regeneracja silnika. Naprawa trwała 3 tygodnie.',
    },
    {
      date: '28.05.2023',
      damageValue: '4200 PLN',
      damageCountry: 'Czechy',
      damageType: AccidentType.HAIL,
      damageLocation: 'Dach i maska',
      mileage: '34 670 km',
      description:
        'Uszkodzenia lakieru i drobne wgniecenia w wyniku intensywnej burzy gradowej. Wymagana naprawa lakieru i wypukiwanie elementów nadwozia. Grad osiągnął średnicę 3-4 cm.',
    },
    {
      date: '03.09.2020',
      damageValue: 'Całkowita strata',
      damageCountry: 'Włochy',
      damageType: AccidentType.THEFT,
      damageLocation: 'Cały pojazd',
      mileage: '95 230 km',
      description:
        'Kradzież pojazdu z monitorowanego parkingu hotelowego. Pojazd odzyskany po 14 dniach z uszkodzeniami mechanicznymi i elektrycznymi. Wymieniono kluczyki i system alarmowy.',
    },
    {
      date: '10.12.2021',
      damageValue: '2800 PLN',
      damageCountry: 'Austria',
      damageType: AccidentType.VANDALISM,
      damageLocation: 'Lusterka boczne',
      mileage: '78 920 km',
      description:
        'Akt wandalizmu na parkingu - uszkodzone oba lusterka boczne oraz zarysowany lakier po prawej stronie pojazdu.',
    },
    {
      date: '17.06.2022',
      damageCountry: 'Holandia',
      damageType: AccidentType.ELECTRICAL_DAMAGE,
      damageLocation: 'Instalacja elektryczna',
      mileage: '112 340 km',
      description:
        'Uszkodzenie instalacji elektrycznej w wyniku zalania. Wymiana wiązki przewodów oraz modułów elektronicznych.',
    },
    {
      date: '25.03.2018',
      damageValue: '5200 PLN',
      damageCountry: 'Belgia',
      damageType: AccidentType.FIRE,
      damageLocation: 'Komora silnika',
      mileage: '45 680 km',
      description:
        'Pożar komory silnika spowodowany nieszczelnością układu paliwowego. Znaczne uszkodzenia elementów plastikowych i przewodów. Wymiana instalacji oraz renowacja komory.',
    },
    {
      date: '12.08.2020',
      damageValue: '18 500 PLN',
      damageCountry: 'Hiszpania',
      damageType: AccidentType.FLOOD,
      damageLocation: 'Podłoga i próg',
      mileage: '89 120 km',
      description:
        'Pojazd ucierpiał w wyniku powodzi. Woda sięgała do poziomu progów. Wymiana tapicerki, osuszanie oraz regeneracja instalacji elektrycznej. Kontrola wszystkich płynów eksploatacyjnych.',
    },
  ],

  // Status prawny
  legal: {
    polishDatabases: [
      {
        name: 'Krajowy rejestr zastawów skarbowych',
        variant: 'danger',
        description:
          'UWAGA! Pojazd figuruje w krajowym rejestrze zastawów skarbowych',
        badgeText: 'Problematyczny',
      },
      {
        name: 'Bazy pojazdów skradzionych',
        variant: 'success',
        description: 'Pojazd nie figuruje w bazach pojazdów kradzionych.',
        badgeText: 'Czysty',
      },
    ],
    verificationDate: '12.03.2024',
    overallStatus: 'warning',
    countries: mockCountries,
  },
};
